import React from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Blocks rendering of a page when the tenant's subscription plan has the
 * underlying Feature-Matrix module/feature disabled. SuperAdmin / exempt
 * tenants are never blocked. Fails OPEN while features are still loading.
 *
 * Props:
 *  - modules:  array of Feature-Matrix module names; access granted if ANY is enabled
 *  - feature:  a single Feature-Matrix feature_key (alternative to modules)
 */
export const FeatureGate = ({ modules, feature, children }) => {
  const { user, moduleEnabled, featureEnabled } = useAuth();

  const isSuper = user?.roles?.includes('SuperAdmin');
  let allowed = true;
  if (!isSuper) {
    if (Array.isArray(modules) && modules.length) {
      allowed = modules.some((m) => moduleEnabled(m));
    } else if (feature) {
      allowed = featureEnabled(feature);
    }
  }

  if (allowed) return children;

  return (
    <div
      className="flex items-center justify-center min-h-[60vh]"
      data-testid="feature-locked"
    >
      <div className="text-center max-w-md p-8 bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
          <Lock className="w-7 h-7 text-amber-500" />
        </div>
        <h2 className="text-lg font-semibold text-slate-800">Not available on your plan</h2>
        <p className="mt-2 text-sm text-slate-500">
          This feature is disabled for your current subscription plan. Upgrade your plan to
          unlock it instantly.
        </p>
        <Link
          to="/billing"
          data-testid="feature-upgrade-cta"
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-600"
        >
          <Sparkles className="w-4 h-4" />
          🔒 Upgrade to unlock
        </Link>
        <p className="mt-3 text-xs text-slate-400">
          Or contact your administrator to enable this feature.
        </p>
      </div>
    </div>
  );
};

export default FeatureGate;
